// import api's and mutations
import { mutations, queries } from 'RestAPIs/rapini';
import {
  DutyAskRoundSettingsResponse,
  AskRoundSettingsResponse
} from 'generated/index';

// import components
import { Typography } from '@mui/material';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import RoundModal from './components/RoundModal/RoundModal';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { StyledPaperFlexColumn } from 'components/shared/Paper/StyledPaper.styles';
import { StyledBoxFlexColumn } from 'components/shared/Box/Box.styles';
import { StyledDisabledOverlayBox, StyledDivider } from './AIMatch.styles';

// import constants
import { defaultRoundValues, defaultSettingValues } from './AIMatch.consts';

// import hooks
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useGetAIMatchSettings from './hooks/useGetAIMatchSettings';
import useUpdateAIMatchSettings from './hooks/useUpdateAIMatchSettings';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'hooks';

// import schema
import schema from './schema';

// import types
import {
  AIMatchKey,
  AIMatchSettingsType,
  AdminSettings,
  PlanUnit,
  Setting
} from 'components/types';
import { AIMatchForm } from './AIMatch.types';

// import utils
import {
  getValuesByAIMatchSettingsType,
  hhmmToMinutes,
  minutesToHHmm
} from './utils';
import AIMatchBasicTopBar from './components/AIMatchBasicTopBar';
import AIMatchNotifications from './components/AIMatchNotifications';
import AIMatchRoundSettings from './components/AIMatchRoundSettings';
import AIMatchFooter from './components/AIMatchFooter';

const AIMatchSettings = () => {
  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });
  const { hasDutyAutoAskEmployeesBasic, hasDutyAutoAskEmployeesAdvanced } =
    useSelector((state) => state.grants);

  // id is hardcoded which is required by the api
  const { data: ConfigurationSettings } = queries.useFindConfigurationById(
    'c9316f55-2fc4-481d-933e-81dd1e62965f'
  );

  const [planUnits, setPlanUnits] = useState<PlanUnit[]>([]);
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnit[]>([]);
  const [pendingCreations, setPendingCreations] = useState<string[]>([]);
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  const defaultSelectedPlanUnits = useMemo(() => {
    if (!ConfigurationSettings) return [];
    const existingEntityIds =
      ConfigurationSettings?.planningEntities?.map((e) => e.id) || [];

    return planUnits.filter((pu) => existingEntityIds.includes(pu.entityId));
  }, [ConfigurationSettings, planUnits]);

  const hasPlanningEntities = selectedPlanUnits.length > 0;

  const methods = useForm<AIMatchForm>({
    defaultValues: schema.cast(defaultSettingValues),
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const { formState, handleSubmit, setValue, getValues, reset } = methods;

  const [currentRound, setCurrentRound] = useState<
    { index: number; round: AskRoundSettingsResponse } | null | undefined
  >(null);

  const { data: aiMatchSettings, isLoading: isLoadingAIMatchSettings } =
    useGetAIMatchSettings();
  const { mutateAsync: updateAIMatchSettings } = useUpdateAIMatchSettings();

  const { isLoading: isLoadingRoundSettings, data: roundSettings } =
    queries.useGetDutyAskRoundSettings();
  const {
    isLoading: isLoadingUpdateRoundSettings,
    mutateAsync: updateRoundSettings
  } = mutations.useUpdateDutyAskRoundSettings();

  const isLoading =
    isLoadingAIMatchSettings ||
    isLoadingRoundSettings ||
    isLoadingUpdateRoundSettings;

  const settings: Setting[] = aiMatchSettings?.settings || [];

  const updateRound = (index: number, round: AskRoundSettingsResponse) => {
    setValue(
      'rounds',
      getValues().rounds.map((r, i) => (i === index ? round : r)),
      { shouldDirty: true }
    );
    setCurrentRound(null);
  };

  const onSubmit = async (formData: AIMatchForm) => {
    const beforeStartMinutes = hhmmToMinutes(
      formData[AIMatchSettingsType.BEFORE_START_LIMIT]
    );

    const roundMinutes = hhmmToMinutes(
      formData[AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]
    );

    const matchSettingsPayload = {
      ...aiMatchSettings,
      settings: (settings || []).map((setting) => {
        if (setting.key === `${AIMatchKey}.${AIMatchSettingsType.TIMES}`) {
          return {
            ...setting,
            value: formData[AIMatchSettingsType.TIMES]
              .map(({ value }) => value)
              .join(',')
          };
        }
        if (
          setting.key ===
          `${AIMatchKey}.${AIMatchSettingsType.BEFORE_START_LIMIT}`
        ) {
          return {
            ...setting,
            value: beforeStartMinutes.toString()
          };
        }
        return setting;
      })
    } as AdminSettings;

    const roundSettingsPayload = {
      minutesBeforeStartLimit: roundMinutes,
      flexEnabled: formData[AIMatchSettingsType.FLEX_ENABLED],
      nonFlexEnabled: formData[AIMatchSettingsType.NON_FLEX_ENABLED],
      rounds: formData.rounds
    } as DutyAskRoundSettingsResponse;

    // NOTE: the following values are hardcoded for now, this should be changed in the future
    const roundsWithHardcodedValues = formData.rounds.map((round) => ({
      ...round,
      minimalMinutesOfAdvantageBeforeNext: 120,
      overlapMargin: 480
    }));

    const updatedRoundSettingsPayload = {
      ...roundSettingsPayload,
      rounds: roundsWithHardcodedValues
    } as DutyAskRoundSettingsResponse;

    try {
      await updateAIMatchSettings(matchSettingsPayload);
      await updateRoundSettings(updatedRoundSettingsPayload);

      reset(formData);
      showSuccessSnackbar();
    } catch {
      showErrorSnackbar();
    }
  };

  useEffect(() => {
    if (aiMatchSettings && roundSettings) {
      const beforeStartSettingValue = +(
        getValuesByAIMatchSettingsType(
          AIMatchSettingsType.BEFORE_START_LIMIT,
          settings
        )?.value || 0
      );
      const beforeStartHHmm = minutesToHHmm(beforeStartSettingValue);

      const roundBeforeStartValue = +roundSettings.minutesBeforeStartLimit || 0;
      const roundHHmm = minutesToHHmm(roundBeforeStartValue);

      const timesSetting = getValuesByAIMatchSettingsType(
        AIMatchSettingsType.TIMES,
        settings
      )?.value;
      const parsedTimes =
        timesSetting?.split(',').map((val) => ({ label: val, value: val })) ||
        [];

      reset(
        schema.cast({
          [AIMatchSettingsType.TIMES]: parsedTimes,
          [AIMatchSettingsType.BEFORE_START_LIMIT]: beforeStartHHmm,
          [AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]: roundHHmm,
          [AIMatchSettingsType.FLEX_ENABLED]:
            roundSettings.flexEnabled ?? false,
          [AIMatchSettingsType.NON_FLEX_ENABLED]:
            roundSettings.nonFlexEnabled ?? false,
          rounds: roundSettings.rounds || []
        })
      );
    }
  }, [aiMatchSettings, roundSettings, settings, reset]);

  useEffect(() => {
    setSelectedPlanUnits(defaultSelectedPlanUnits);
  }, [defaultSelectedPlanUnits]);

  const onPlanUnitSelected = async (newSelection: PlanUnit[]) => {
    if (!hasUserInteracted && defaultSelectedPlanUnits.length > 0) {
      setHasUserInteracted(true);
      setSelectedPlanUnits(newSelection);
      return;
    }

    const oldIds = selectedPlanUnits
      .map((p) => p.entityId)
      .filter((id): id is string => id !== undefined);

    const newIds = newSelection
      .map((p) => p.entityId)
      .filter((id): id is string => id !== undefined);

    const newlyAdded = newIds.filter((id) => !oldIds.includes(id));
    const removed = oldIds.filter((id) => !newIds.includes(id));

    setPendingCreations((prev) => [...prev, ...newlyAdded]);
    setPendingDeletions((prev) => [...prev, ...removed]);

    setSelectedPlanUnits(newSelection);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledBoxFlexColumn>
            <Typography variant="h6">{t('title')}</Typography>
            <AIMatchBasicTopBar
              hasDutyAutoAskEmployeesBasic={hasDutyAutoAskEmployeesBasic}
              hasDutyAutoAskEmployeesAdvanced={hasDutyAutoAskEmployeesAdvanced}
              setPendingCreations={setPendingCreations}
              setPendingDeletions={setPendingDeletions}
              onPlanUnitSelected={onPlanUnitSelected}
              setPlanUnits={setPlanUnits}
              defaultSelectedPlanUnits={defaultSelectedPlanUnits}
              pendingCreations={pendingCreations}
              pendingDeletions={pendingDeletions}
              selectedPlanUnits={selectedPlanUnits}
              setSelectedPlanUnits={setSelectedPlanUnits}
            />

            <StyledPaperFlexColumn>
              {!hasPlanningEntities && !hasDutyAutoAskEmployeesAdvanced && (
                <StyledDisabledOverlayBox />
              )}
              {/* Round settings */}
              <AIMatchRoundSettings
                isLoading={isLoading}
                setCurrentRound={setCurrentRound}
                defaultRoundValues={defaultRoundValues}
                hasPlanningEntities={hasPlanningEntities}
                hasDutyAutoAskEmployeesAdvanced
              />
              <StyledDivider />
              {/* Notification settings */}
              <AIMatchNotifications isLoading={isLoading} />

              <AIMatchFooter formState={formState} />
            </StyledPaperFlexColumn>
          </StyledBoxFlexColumn>
        </form>
      </FormProvider>

      <RoundModal
        round={currentRound}
        isOpen={!!currentRound}
        onSave={updateRound}
        onClose={() => setCurrentRound(null)}
        isEdit={!!currentRound}
      />

      <LoadingOverlay loading={isLoading} />
    </>
  );
};

export default AIMatchSettings;
